// AYA Search V2 — Fiche de découverte géographique, version immersive sans contours.
// Héro compact (photo + titre typé + description dépliable) → carte réductible + transport → mairie → commerces (mini-cartes) → lieux associés (photos) → liens web.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Landmark, ArrowRight, Store, Globe, ChevronRight, Map as MapIcon, Route, Wind, Droplets, Maximize2, Minimize2, BadgeCheck, ShoppingCart, Wrench, Flower2, Car, KeyRound, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { LocalPackMap } from "@/components/LocalPackMap";
import "./place.css";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` } });

const SecIcon = ({ children, color = "#0E8A4F" }) => (
  <span className="gd-ico" style={{ background: color }}>{children}</span>
);

// Catégories de l'accueil AYA — mêmes verticales, design façon tuiles transport.
const HOME_CATS = [
  { v: "commerce", label: "Commerces", route: "/commerce", Icon: ShoppingCart, color: "#F97316", bg: "rgba(249,115,22,0.10)" },
  { v: "services", label: "Services", route: "/services", Icon: Wrench, color: "#FB923C", bg: "rgba(249,115,22,0.10)" },
  { v: "bienetre", label: "Bien-être", route: "/bien-etre", Icon: Flower2, color: "#F472B6", bg: "rgba(236,72,153,0.10)" },
  { v: "transport", label: "Transport", route: "/transport", Icon: Car, color: "#0E8A4F", bg: "rgba(16,185,129,0.12)" },
  { v: "location", label: "Location", route: "/location", Icon: KeyRound, color: "#F97316", bg: "rgba(249,115,22,0.10)" },
  { v: "sante", label: "Santé", route: "/sante", Icon: Plus, color: "#EF5350", bg: "rgba(225,60,80,0.10)" },
  { v: "administration", label: "Services publics", route: "/services-publics", Icon: Landmark, color: "#0E7A4F", bg: "rgba(16,185,129,0.10)" },
];

export default function PlaceDiscovery({ place, navigate, onSearch, onSeeCommerces, commerceCount, commerces = [] }) {
  const [descOpen, setDescOpen] = useState(false);
  const [fullHistory, setFullHistory] = useState(false);
  const [richHistory, setRichHistory] = useState(null); // null | "loading" | { text, sources }
  const [weather, setWeather] = useState(null);
  const [mapBig, setMapBig] = useState(false);
  const [groups, setGroups] = useState(null); // { commerces, hotels, loisirs }
  const [followed, setFollowed] = useState(false);

  useEffect(() => {
    setWeather(null); setRichHistory(null); setFullHistory(false); setDescOpen(false); setMapBig(false); setGroups(null);
    if (!place) return;
    axios.get(`${API}/api/v3/search/place-weather`, { params: { slug: place.slug } })
      .then((r) => r.data?.ok && setWeather(r.data.weather))
      .catch(() => {}); // météo indisponible → pastille masquée, rien ne bloque
    axios.get(`${API}/api/v3/search/place-shops`, { params: { slug: place.slug } })
      .then((r) => r.data?.ok && setGroups(r.data.groups))
      .catch(() => {});
    setFollowed(false);
    axios.get(`${API}/api/v3/search/follow-places`, authHdr())
      .then((r) => r.data?.ok && setFollowed(r.data.places.some((x) => x.slug === place.slug)))
      .catch(() => {});
  }, [place?.slug]); // eslint-disable-line

  const openHistory = () => {
    if (fullHistory) { setFullHistory(false); return; }
    setFullHistory(true);
    if (richHistory === null) {
      setRichHistory("loading");
      axios.get(`${API}/api/v3/search/place-history`, { params: { slug: place.slug } })
        .then((r) => setRichHistory(r.data?.ok ? r.data.history : { text: place.history, sources: [] }))
        .catch(() => setRichHistory({ text: place.history, sources: [] }));
    }
  };

  if (!place) return null;
  const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lng}`;
  const T = place.titles || { display: place.name, de: `de ${place.name}`, at: `à ${place.name}`, bare: place.name };
  const commerceRow = (groups?.commerces?.length ? groups.commerces : (commerces || []).filter((c) => c && c.name)).slice(0, 8);
  const shopRows = [
    ["commerces", "Commerces", commerceRow],
    ["hotels", "Hôtels et résidences", (groups?.hotels || []).slice(0, 8)],
    ["loisirs", "Activités et loisirs", (groups?.loisirs || []).slice(0, 8)],
  ];
  // Épingles : coordonnées réelles AYA uniquement, et à proximité du lieu (pas d'homonymes lointains).
  const radiusKm = place.type === "quartier" ? 6 : 20;
  const near = (la, ln) => {
    const dLat = (la - place.lat) * 110.57, dLng = (ln - place.lng) * 111.32 * Math.cos((place.lat * Math.PI) / 180);
    return Math.sqrt(dLat * dLat + dLng * dLng) <= radiusKm;
  };
  const seenIds = new Set();
  const shopPins = [...(commerces || []), ...(groups?.commerces || []), ...(groups?.hotels || []), ...(groups?.loisirs || [])]
    .filter((c) => c && c.id && !seenIds.has(c.id) && seenIds.add(c.id) && Number.isFinite(c.lat) && Number.isFinite(c.lng) && near(c.lat, c.lng))
    .map((c) => ({ id: c.id, name: c.name, lat: c.lat, lng: c.lng, pin_color: "#F97316", link: c.link }));
  const mapPins = [...place.pois, ...shopPins];

  const toggleFollow = async () => {
    try {
      if (followed) {
        await axios.delete(`${API}/api/v3/search/follow-places/${place.slug}`, authHdr());
        setFollowed(false); toast.success(`Vous ne suivez plus ${T.bare}`);
      } else {
        await axios.post(`${API}/api/v3/search/follow-places`, { slug: place.slug }, authHdr());
        setFollowed(true); toast.success(`${T.bare} ajoutée à vos villes suivies — météo et actualités sur votre accueil`);
      }
    } catch { toast.error("Connecte-toi pour suivre un lieu"); }
  };

  return (
    <div className="gd-fiche" data-testid={`place-discovery-${place.slug}`}>
      {/* 1. Héro compact : photo + titre typé + description courte dépliable */}
      <div className="gd-sec" data-testid="place-presentation">
        <div className="gd-hero">
          <img src={place.image} alt={place.name} className="gd-hero-img" />
          <div className="gd-hero-txt">
            <h3 className="gd-title">{T.display}</h3>
            <div className="gd-typeline">{place.type_label}</div>
            {weather && (
              <div className="gd-weather" data-testid="place-weather">
                <span className="em">{weather.emoji}</span>
                <b>{weather.temp}°C</b>
                <span className="lb">{weather.label}</span>
                <span className="xt"><Droplets size={11} /> {weather.humidity}%</span>
                <span className="xt"><Wind size={11} /> {weather.wind} km/h</span>
              </div>
            )}
            <p className={`gd-desc gd-clamp ${descOpen ? "open" : ""}`}>{place.desc}{descOpen ? ` ${place.side_note || ""}` : ""}</p>
            <button className="gd-toggle" data-testid="place-desc-toggle" onClick={() => setDescOpen(!descOpen)}>
              {descOpen ? "Réduire" : "Dérouler la suite"} <ChevronRight size={12} style={{ transform: descOpen ? "rotate(-90deg)" : "rotate(90deg)" }} />
            </button>
          </div>
        </div>
        <div className="gd-herofoot">
          <div className="gd-chips">{place.tags.map((t) => <span key={t} className="gd-chip">{t}</span>)}</div>
          <button className={`gd-outline sm ${followed ? "on" : ""}`} data-testid="place-follow-btn" onClick={toggleFollow}>
            <Star size={13} fill={followed ? "currentColor" : "none"} /> {followed ? "Suivie" : "Suivre"}
          </button>
          <button className="gd-outline sm" data-testid="place-history-btn" onClick={openHistory}>
            {fullHistory ? "Réduire l'histoire" : "Lire toute l'histoire"} <ArrowRight size={13} />
          </button>
        </div>
        {fullHistory && (
          <div className="gd-hist" data-testid="place-history-full">
            {richHistory === "loading" ? (
              <p className="gd-desc" style={{ margin: 0 }}>Rédaction du récit sourcé en cours…</p>
            ) : (
              <>
                {String(richHistory?.text || place.history).split(/\n+/).map((par, i) => <p className="gd-desc" style={{ marginTop: i ? 7 : 0 }} key={i}>{par}</p>)}
                {richHistory?.sources?.length > 0 && (
                  <div className="gd-histsrc" data-testid="place-history-sources">
                    <b>Sources :</b>
                    {richHistory.sources.map((s) => (
                      <a key={s.url} href={s.url} target="_blank" rel="noreferrer">{s.domain || s.title}</a>
                    ))}
                  </div>
                )}
                <div className="gd-prov" style={{ marginTop: 6 }}>Récit rédigé par AYA à partir des sources citées — à recouper pour tout usage de référence.</div>
              </>
            )}
          </div>
        )}
        <div className="gd-prov">Présentation éditoriale AYA — sources détaillées dans « Liens du web ».</div>
      </div>

      {/* 2. Carte compacte agrandissable + transport juste en dessous */}
      <div className="gd-sec" data-testid="place-map-section">
        <div className={`gd-map ${mapBig ? "big" : ""}`}>
          <LocalPackMap results={mapPins} selectedId={null} onSelect={(id) => { const c = shopPins.find((s) => s.id === id); if (c?.link) navigate(c.link); }} />
          <button className="gd-mapzoom" data-testid="place-map-expand" onClick={() => setMapBig(!mapBig)}>
            {mapBig ? <Minimize2 size={13} /> : <Maximize2 size={13} />} {mapBig ? "Réduire" : "Agrandir"}
          </button>
        </div>
        {shopPins.length > 0 && (
          <div className="gd-maplegend" data-testid="place-map-legend">
            <span className="dotg" /> {T.bare} <span className="doto" /> {shopPins.length} commerçant{shopPins.length > 1 ? "s" : ""} référencé{shopPins.length > 1 ? "s" : ""} — toucher une épingle ouvre la fiche
          </div>
        )}
        <div className="gd-transrow" data-testid="place-transport">
          {place.transport.map((t) => (
            <div key={t.id} className="gd-trans" data-testid={`place-transport-${t.id}`}>
              <span className="em">{t.emoji}</span><b>{t.label}</b><span>{t.desc}</span>
            </div>
          ))}
        </div>
        <div className="gd-maplinks">
          <a className="gd-cta sm" href={dirUrl} target="_blank" rel="noreferrer" data-testid="place-itineraries-btn"><Route size={13} /> Itinéraires</a>
          <a className="gd-toggle" href={`https://www.google.com/maps/search/?api=1&query=${place.lat},${place.lng}`} target="_blank" rel="noreferrer" data-testid="place-explore-map-btn"><MapIcon size={12} /> Explorer la carte</a>
          <button className="gd-toggle" data-testid="place-transport-all" onClick={() => navigate("/transport")}>Toutes les options <ArrowRight size={12} /></button>
        </div>
      </div>

      {/* 3. Mairie / autorité locale */}
      {place.mairie && (
        <div className="gd-sec" data-testid="place-mairie">
          <div className="gd-head">
            <SecIcon><Landmark size={15} color="#fff" /></SecIcon>
            <div><h3>{place.mairie.name}</h3><p>{place.mairie.line}</p></div>
            <button className="gd-more" onClick={() => place.mairie.slug ? navigate(`/mairie/${place.mairie.slug}`) : onSearch(place.mairie.name.toLowerCase())}>Voir la fiche <ArrowRight size={12} /></button>
          </div>
          <div className="gd-mairiegrid">
            <img src={place.mairie.image} alt="" className="gd-img tall" />
            <div>
              <p className="gd-desc" style={{ marginTop: 0 }}>{place.mairie.desc}</p>
              <div className="gd-btnrow">
                <button className="gd-cta sm" data-testid="place-mairie-fiche-btn" onClick={() => place.mairie.slug ? navigate(`/mairie/${place.mairie.slug}`) : onSearch(place.mairie.name.toLowerCase())}>Voir la fiche <ArrowRight size={13} /></button>
                <a className="gd-outline sm" href={dirUrl} target="_blank" rel="noreferrer" data-testid="place-mairie-itinerary-btn"><MapPin size={13} /> Itinéraire</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4. Commerces, hôtels & résidences, activités & loisirs — mini-cartes au format « Lieux à… » */}
      <div className="gd-sec" data-testid="place-commerces">
        <div className="gd-head">
          <SecIcon color="#F0590B"><Store size={15} color="#fff" /></SecIcon>
          <div><h3>Commerces {T.de}</h3><p>Boutiques, hôtels, activités et plus encore</p></div>
          <button className="gd-more" data-testid="place-see-commerces-btn" onClick={onSeeCommerces}>Voir tous <ArrowRight size={12} /></button>
        </div>
        {shopRows.some(([, , items]) => items.length > 0) ? (
          shopRows.map(([key, label, items]) => items.length > 0 && (
            <div key={key}>
              <div className="gd-subhead">{label} <i>{items.length}</i></div>
              <div className="gd-relrow" data-testid={`place-shops-${key}`}>
                {items.map((c) => (
                  <button key={c.id} className="gd-relcard shop" data-testid={`place-shop-${c.id}`} onClick={() => (c.link ? navigate(c.link) : onSeeCommerces())}>
                    {c.image ? <img src={c.image} alt="" /> : <span className="ph">{c.category_emoji || "🏪"}</span>}
                    <b>{c.verified && <BadgeCheck size={11} color="#2563EB" style={{ verticalAlign: -1, marginRight: 3 }} />}{c.name}</b>
                    <span>{[c.category_label, c.commune_name].filter(Boolean).join(" · ")}</span>
                  </button>
                ))}
              </div>
            </div>
          ))
        ) : (
          <p className="gd-desc" style={{ margin: 0 }}>Les établissements référencés {T.at} s'affichent au fur et à mesure de leur intégration dans AYA.</p>
        )}
        <div className="gd-subhead">Explorer par catégorie</div>
        <div className="gd-catrow" data-testid="place-categories">
          {HOME_CATS.map(({ v, label, route, Icon, color, bg }) => (
            <button key={v} className="gd-cat" data-testid={`place-cat-${v}`} onClick={() => navigate(`${route}?ville=${encodeURIComponent(T.bare)}`)}>
              <span className="ic" style={{ background: bg }}><Icon size={19} color={color} /></span>
              <b>{label}</b>
            </button>
          ))}
        </div>
        {commerceCount > 0 && (
          <div className="gd-prov">{commerceCount} établissements référencés dans AYA — fiches complètes juste en dessous.</div>
        )}
      </div>

      {/* 5. Quartiers et lieux associés — cartes avec photos */}
      {place.related.length > 0 && (
        <div className="gd-sec" data-testid="place-related">
          <div className="gd-head">
            <SecIcon><MapIcon size={15} color="#fff" /></SecIcon>
            <div><h3>Quartiers et lieux associés</h3><p>À découvrir autour {T.de}</p></div>
          </div>
          <div className="gd-relrow">
            {place.related.map((r) => (
              <button key={r.slug} className="gd-relcard" data-testid={`place-related-${r.slug}`} onClick={() => onSearch(r.name.toLowerCase())}>
                <img src={r.image} alt="" />
                <b>{r.name}</b>
                <span><MapPin size={10} color="#0E8A4F" /> {r.type_word}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 6. Liens du web (URLs réelles uniquement) */}
      <div className="gd-sec" data-testid="place-weblinks">
        <div className="gd-head">
          <SecIcon color="#2563EB"><Globe size={15} color="#fff" /></SecIcon>
          <div><h3>Liens du web</h3><p>Sites officiels et contenus fiables</p></div>
        </div>
        {place.links.map((l) => (
          <a key={l.url} className="gd-link" href={l.url} target="_blank" rel="noreferrer" data-testid={`place-link-${l.domain}`}>
            <span className="gd-fav"><Globe size={15} color="#2563EB" /></span>
            <span className="bd">
              <b>{l.title}</b>
              <span className="u">{l.domain}</span>
              <span className="d">{l.desc}</span>
            </span>
            <ChevronRight size={16} color="#9CA3AF" />
          </a>
        ))}
      </div>
    </div>
  );
}
